import type { AxiosError } from 'axios';
import axios from 'axios';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { ErrorMessage } from '../models/error';
import type { AsyncThunkConfig } from '../models/slice';
import { RaygunErrorHandlerService } from './raygun.service';

const { logError } = RaygunErrorHandlerService();

type MoodFilter = {
  value: string;
  text: string;
};

type Product = {
  product_id: string;
  organization_id: string;
  name: string;
  order: number;
  short_name: string;
};

type ProductFilter = {
  value: string;
  text: string;
};

type TableSliceType = {
  moodFilter: MoodFilter[];
  productFilter: ProductFilter[];
};

const initialState: TableSliceType = {
  moodFilter: [],
  productFilter: [],
};

export const getMoods = createAsyncThunk<MoodFilter[], undefined, AsyncThunkConfig>(
  'table/getMoods',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get('v3_mood', { signal: thunkAPI.signal });
      return (response as unknown as MoodFilter[]) ?? [];
    } catch (e) {
      logError(e, ['tableSlice', 'getMoods']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getOrganizationProducts = createAsyncThunk<Product[], undefined, AsyncThunkConfig>(
  'table/getOrganizationProducts',
  async (_, thunkAPI) => {
    try {
      const authSlice = thunkAPI.getState().authSlice;
      const response = await axios.post('v3_get_organization_product', undefined, { signal: thunkAPI.signal });
      if (response) {
        if (authSlice.authResult.use_cravings_quick_checkin && authSlice.currentUser.organization_id) {
          const withCravings = response as unknown as Product[];
          withCravings.push({
            product_id: '9e06d699-411d-4155-baa6-789fab8d0a00',
            organization_id: authSlice.currentUser.organization_id,
            name: 'Cravings',
            order: 100,
            short_name: 'Cravings',
          });
          return withCravings;
        } else {
          return response as unknown as Product[];
        }
      }
      return [];
    } catch (e) {
      logError(e, ['tableSlice', 'getOrganizationProducts']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const tableSlice = createSlice({
  name: 'table',
  initialState,
  reducers: {
    // standard reducer logic, with auto-generated action types per reducer
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMoods.fulfilled, (state, action) => {
        state.moodFilter = action.payload;
      })
      .addCase(getOrganizationProducts.fulfilled, (state, action) => {
        state.productFilter = action.payload.map(({ short_name }) => ({
          value: short_name,
          text: short_name,
        }));
      });
  },
});

// export const {} = tableSlice.actions;
